import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
	EnvironmentOutlined,
	ArrowLeftOutlined,
	PlusCircleOutlined,
	StarOutlined,
	EditOutlined,
	DeleteOutlined,
	NodeIndexOutlined,
} from '@ant-design/icons';
import {
	Form,
	Table,
	Tooltip,
	notification,
} from 'antd';
import { Input, Toggle, Button } from '@hydrodigital/design-system';
import type { ColumnsType } from 'antd/es/table';
import { setSelectedParameterDataToDigitalTwin } from '@/redux/slices/digitalTwinSlice';
import InfrastructureService from '../../../domain/Infrastructure/InfraestructureService';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import ParameterService from '../../../domain/Parameter/ParameterService';
import ModalParameterDetail from '../../../components/Parameters/Infrastructure/ParameterDetail';
import AddInfrastructureModal from '../../../components/Infrastructure';
import InfraestructureContext from '@/contexts/InfraestructureContext';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { infraTypes, infraTypesById } from '../../../statics/constants/infraTypes';
import { VirtualSource } from '@/domain/VirutalSources/VirtualSource';
import { Infrastructure } from '@/domain/Infrastructure/Infrastructure';
import { selectUnitsByTypeId } from '@/redux/slices/measurementTypesWithUnitsSlice';
import { StreetViewTokenService } from '@/services/streetViewToken';

const infrastructureService = new InfrastructureService();
const parameterService = new ParameterService();

interface InfraestructureValueInterface {
	selectedParam: any | null;
	selectedVirtualSource: VirtualSource | null;
	showAddVariableVirtualParam: boolean;
	handleAddVariableVirtualParam: Function | null;
	locationName: string;
}

const ParameterView: React.FC = () => {
	const { infraId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const typesWithUnits = useSelector((state: any) => state.typesWithUnits.array);
	const [contextValue, setContextValue] = useState<InfraestructureValueInterface>({
		selectedParam: null,
		selectedVirtualSource: null,
		showAddVariableVirtualParam: false,
		handleAddVariableVirtualParam: null,
		locationName: '',
	});
	const [infrastructure, setInfrastructure] = useState<Infrastructure | null>(null);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [showModalParameterDetail, setShowModalParameterDetail] = useState(false);

	const [isNewParameter, setIsNewParameter] = useState<boolean>(true);
	const [editParameter, setEditParamater] = useState<any>({});

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [description, setDescription] = useState(infrastructure?.description || '');
	const [showEditInfraName, setShowEditInfraName] = useState(false);
	const [parameterEditSnapshots, setParameterEditSnapshots] = useState<Record<number, any>>({});
	const locationId = useRef<any>(null);
	const [form] = Form.useForm();

	const resolveEffectiveForecastUnit = (parameter: any, snapshot?: any) => {
		const currentTypeUnits = selectUnitsByTypeId(typesWithUnits, parameter?.paramTypeId ?? 0);
		const savedForecastUnitId = parameter?.parameterForecastUnitId ?? null;
		const savedForecastUnitName = parameter?.parameterForecastUnitName ?? null;
		const savedForecastUnitBelongsToCurrentType =
			savedForecastUnitId != null &&
			currentTypeUnits.some((unit: any) => `${unit.id}` === `${savedForecastUnitId}`);
		const previousParamTypeId = snapshot?.previousParamTypeId ?? null;
		const previousParamUnitId = snapshot?.previousParamUnitId ?? null;
		const previousParamUnitName = snapshot?.previousParamUnitName ?? null;
		const previousEffectiveForecastUnitId = snapshot?.previousEffectiveForecastUnitId ?? null;
		const previousEffectiveForecastUnitName = snapshot?.previousEffectiveForecastUnitName ?? null;
		const typeChanged =
			previousParamTypeId != null &&
			`${previousParamTypeId}` !== `${parameter?.paramTypeId ?? null}`;
		const unitChanged =
			previousParamUnitId != null &&
			`${previousParamUnitId}` !== `${parameter?.paramUnitId ?? null}`;

		if (parameter?.parameterForecastUnitIsCustom) {
			return {
				effectiveForecastUnitId: null,
				effectiveForecastUnitName: 'Custom',
			};
		}

		if (savedForecastUnitBelongsToCurrentType) {
			return {
				effectiveForecastUnitId: savedForecastUnitId,
				effectiveForecastUnitName:
					savedForecastUnitName || `Unit ${savedForecastUnitId}`,
			};
		}

		if (typeChanged && unitChanged) {
			return {
				effectiveForecastUnitId: parameter?.paramUnitId ?? null,
				effectiveForecastUnitName:
					parameter?.parameterUnit || `Unit ${parameter?.paramUnitId ?? ''}`,
			};
		}

		if (previousEffectiveForecastUnitId != null) {
			return {
				effectiveForecastUnitId: previousEffectiveForecastUnitId,
				effectiveForecastUnitName:
					previousEffectiveForecastUnitName || `Unit ${previousEffectiveForecastUnitId}`,
			};
		}

		if (previousParamUnitId != null) {
			return {
				effectiveForecastUnitId: previousParamUnitId,
				effectiveForecastUnitName:
					previousParamUnitName || `Unit ${previousParamUnitId}`,
			};
		}

		return {
			effectiveForecastUnitId: parameter?.paramUnitId ?? null,
			effectiveForecastUnitName:
				parameter?.parameterUnit || `Unit ${parameter?.paramUnitId ?? ''}`,
		};
	};

	const attachParameterEditSnapshot = (parameter: any) => {
		if (!parameter?.id) {
			return parameter;
		}

		const snapshot = parameterEditSnapshots[parameter.id];
		const mergedParameter = snapshot ? { ...parameter, ...snapshot } : parameter;
		return {
			...mergedParameter,
			...resolveEffectiveForecastUnit(mergedParameter, snapshot),
		};
	};

	const onSaveInfraName = async () => {
		const newInfraName = form.getFieldValue('infraName');
		if (infraId && newInfraName) {
			const response = await infrastructureService.putInfraestructureDescription(
				infraId,
				description,
				newInfraName,
			);
			console.log('🚀 ~ handleUpdateDescription ~ response:', response);
			if (response.success == 1) {
				notification.success({
					message: 'Edited successfully',
          description: 'Asset name edited successfully',
					placement: 'topRight',
				});
				await handleGetInfrastructure();
				setShowEditInfraName(false);
			}
		}
	};

	const showModal = () => {
		setIsModalOpen(true);
	};

	const handleOk = () => {
		setIsModalOpen(false);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		setContextValue({ ...contextValue, selectedParam: null });
	};

	async function handleUpdateMainParameter(parameterId: string | number) {
		await parameterService.putFavoriteParameter(parameterId);
		await handleGetInfrastructure();
	}

	async function handleUpdateDescription() {
		if (infraId && infrastructure) {
			const response = await infrastructureService.putInfraestructureDescription(
				infraId,
				description,
				infrastructure.name,
			);
			console.log('🚀 ~ handleUpdateDescription ~ response:', response);
			if (response.success == 1) {
				notification.success({
					message: 'Edited successfully',
					description: 'Parameter description edited successfully',
					placement: 'topRight',
				});
				setShowEditInfraName(false);
			}
		}
	}

	const columnsTable: ColumnsType<any> = [
		{
			title: 'Parameter Name',
			dataIndex: 'name',
			key: 'name',
			fixed: 'left',
			render: (_, { name, id }) => {
				return (
					<span>
						{name} <br />
						<span style={{ color: '#023E65', fontSize: 13, fontWeight: 5 }}>ID: {id} </span>
					</span>
				);
			},
		},
		{
			title: 'Main Parameter',
			dataIndex: 'isMainParameter',
			key: 'isMainParameter',
			render: (_, record) => (
				<StarOutlined
					style={{
						color: record.principal ? '#078CB3' : '#C4CCD5',
						cursor: 'pointer',
					}}
					onClick={() => {
						handleUpdateMainParameter(record.id);
					}}
					rev={undefined}
				/>
			),
		},
		{
			title: 'Critical High',
			dataIndex: 'highCritical',
			key: 'highCritical',
		},
		{
			title: 'Critical High High',
			dataIndex: 'hHighCritical',
			key: 'hHighCritical',
		},
		{
			title: 'Critical Low',
			dataIndex: 'lowCritical',
			key: 'lowCritical',
		},
		{
			title: 'Critical Low Low',
			dataIndex: 'lLowCritical',
			key: 'lLowCritical',
		},
		{
			title: 'Sample rate',
			dataIndex: 'sampleRate',
			key: 'sampleRate',
		},
		{
			title: 'Transfer rate',
			dataIndex: 'transferRate',
			key: 'transferRate',
		},
		{
			title: 'Expiration time',
			dataIndex: 'expirationTime',
			key: 'expirationTime',
		},
		{
			title: 'Parameter type',
			dataIndex: 'parameterType',
			key: 'parameterType',
		},
		{
			title: 'Display Unit',
			dataIndex: 'parameterUnit',
			key: 'parameterUnit',
		},
		{
			title: (
				<div>
					<Tooltip title="For an active infrastructure, it must have an active source and parameter">
						<span>Visible</span>
					</Tooltip>
				</div>
			),
			key: 'isVisible',
			render: (_, record) => (
				<Toggle
					checkedText="ON"
					uncheckedText="OFF"
					size="small"
					checked={record.isVisible}
					onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
						const checked = event.target.checked;
						try {
							if (checked) {
								await parameterService.enableParameter(record);
							} else {
								await parameterService.disableParameter(record);
							}
							if (infrastructure) {
								const newData: any = [...infrastructure.parameters];
								const index = newData.findIndex((item: any) => record.id === item.id);
								newData[index].isVisible = checked;
								console.log('🚀 ~ onClick={ ~ newData:', newData);
								setInfrastructure({ ...infrastructure, parameters: newData });
							}
						} catch (error) {
							console.error(error);
						}
					}}
				/>
			),
		},
		{
			title: 'Sources',
			key: 'viewResource',
			render: (_, record) => (
				<span
					onClick={() => {
						setContextValue({ ...contextValue, selectedParam: attachParameterEditSnapshot(record) });
						console.log('🚀 ~ file: index.tsx:142 ~ record:', record);
						// Agregar parameterId como parámetro en la URL
						setSearchParams((prev) => {
							const newParams = new URLSearchParams(prev);
							newParams.set('parameterId', record.id.toString());
							return newParams;
						});
						setShowModalParameterDetail(true);
					}}
					style={{
						cursor: 'pointer',
						textAlign: 'center',
						textDecorationLine: 'underline',
						color: '#22A2CA',
					}}
				>
					View Sources
				</span>
			),
		},
		{
			title: 'Actions',
			key: 'edit',
			render: (_, record) => (
				<div className="flex">
					<Tooltip title="Edit" className="mr-2">
						<EditOutlined
							style={{ cursor: 'pointer', fontSize: '1.5rem' }}
							onClick={() => {
								const nextSnapshot = {
									previousParamTypeId: record.paramTypeId ?? null,
									previousParamUnitId: record.paramUnitId ?? null,
									previousParamUnitName: record.parameterUnit ?? null,
									previousEffectiveForecastUnitId: record.effectiveForecastUnitId ?? null,
									previousEffectiveForecastUnitName: record.effectiveForecastUnitName ?? null,
								};
								setParameterEditSnapshots((prev) => ({
									...prev,
									[record.id]: nextSnapshot,
								}));
								setContextValue({
									...contextValue,
									selectedParam: {
										...record,
										...nextSnapshot,
									},
								});
								setIsNewParameter(false);
								showModal();
							}}
							rev={undefined}
						/>
					</Tooltip>
					<Tooltip title="Delete" className="mr-2">
						<DeleteOutlined
							style={{ cursor: 'pointer', fontSize: '1.5rem' }}
							onClick={() => {
								setContextValue({ ...contextValue, selectedParam: record });
								setShowConfirmDelete(true);
							}}
							rev={undefined}
						/>
					</Tooltip>
					<Tooltip title="HydroTwin">
						<NodeIndexOutlined
							style={{ cursor: 'pointer', fontSize: '1.5rem' }}
							onClick={() => {
								dispatch(
									setSelectedParameterDataToDigitalTwin({
										parameterId: record.id,
										infrastructureId: infraId,
										locationId: infrastructure?.locationId,
										parameterName: record.name,
										locationName: infrastructure?.locationName,
									}) as unknown as AnyAction,
								);
								navigate(`/infrastructures/parameter/hydroTwin`);
							}}
							rev={undefined}
						/>
					</Tooltip>
				</div>
			),
		},
	];

	const handleGetInfrastructure = async () => {
		if (infraId) {
			const infra = await infrastructureService.getInfrastructureWithParams(infraId);
			const infraWithSnapshots = {
				...infra,
				parameters: infra.parameters.map((parameter: any) => attachParameterEditSnapshot(parameter)),
			};
			setInfrastructure(infraWithSnapshots);
			locationId.current = infraWithSnapshots.locationId;
			form.setFieldValue('infraName', infraWithSnapshots.name);
			if (contextValue.selectedParam) {
				const refreshedSelectedParam =
					infraWithSnapshots.parameters.find((p) => p.id == contextValue.selectedParam?.id) || null;
				
				setContextValue({
					...contextValue,
					locationName: infraWithSnapshots.locationName,
					selectedParam: refreshedSelectedParam,
				});
			}
		} else {
			navigate(`parameters`);
		}
	};

	useEffect(() => {
		handleGetInfrastructure();
		StreetViewTokenService.prefetch();
	}, []);

	useEffect(() => {
		setDescription(infrastructure?.description || '');
	}, [infrastructure]);

	useEffect(() => {
		const parameterId = searchParams.get('parameterId');
		if (parameterId && infrastructure?.parameters) {
			const parameter = infrastructure.parameters.find((p) => p.id.toString() === parameterId);
			if (parameter) {
				setContextValue((prevContext) => ({
					...prevContext,
					selectedParam: attachParameterEditSnapshot(parameter),
					locationName: infrastructure.locationName,
				}));
				setShowModalParameterDetail(true);
			}
		}
	}, [infrastructure, searchParams]);

	return (
		<InfraestructureContext.Provider value={{ value: contextValue, setValue: setContextValue }}>
			<>
				<div style={{ padding: '0px 3rem' }}>
					<Button
						size="small"
						label="Back to Assets"
						iconName="arrow-left"
						iconColor="#434B56"
						style={{ backgroundColor: '#F7F9FA', color: '#434B56', border: 0 }}
						onClick={() => {
							navigate('/infrastructures');
						}}
					/>

					<div className="flex column pt-2">
						{showEditInfraName ? (
							<div className="flex">
								<Form
									form={form}
									layout="horizontal"
									autoComplete="off"
									size="small"
									onFinish={onSaveInfraName}
									onFinishFailed={() => {}}
								>
									<div className="flex mr-2">
										<Form.Item
											name="infraName"
											rules={[{ required: true, message: 'Please enter a name' }]}
										>
											<Input style={{ height: 40 }} />
										</Form.Item>
										<div className="flex justify-between ml-2">
											<div className="mr-3">
												<Button
													label="Save"
													showIcon={false}
													style={{
														width: '85px',
														height: '40px',
														backgroundColor: '#00203C',
														color: 'white',
													}}
													onClick={() => form.submit()}
												/>
											</div>

											<Button
												label="Cancel"
												showIcon={false}
												style={{ backgroundColor: '#F7F9FA', color: '#434B56', border: 0 }}
												onClick={() => setShowEditInfraName(false)}
											/>
										</div>
									</div>
								</Form>
							</div>
						) : (
							<div className="flex">
								<span className="text-title-page">{infrastructure?.name}</span>
								<Tooltip title="Edit">
									<Button
										size="small"
										showIcon={true}
										showLabel={false}
										iconName="edit-alt"
										style={{ borderRadius: '50px', height: 24, width: 24, padding: 0 }}
										onClick={() => {
											setShowEditInfraName(true);
										}}
									/>
								</Tooltip>
							</div>
						)}
						{infrastructure && (
							<span style={{ fontSize: '1.1rem' }}>
								{infrastructure.infraTypeId
									? infraTypesById[infrastructure.infraTypeId as keyof typeof infraTypesById]
									: ''}
							</span>
						)}
						<div style={{ color: '#023E65' }} className="pt-2">
							<EnvironmentOutlined style={{ fontSize: '1.3rem' }} rev={undefined} />
							<span style={{ fontSize: '1.1rem' }} className="ml-1">
								{infrastructure?.locationName}
							</span>
						</div>
						<div className="flex column pt-2">
							<Input
								as="textarea"
								value={description}
								maxLength={100}
								style={{
									height: 80,
									resize: 'none',
									border: '1px solid #d9d9d9',
									background: '#ffffff',
									borderRadius: 5,
								}}
								onChange={(e) => {
									setDescription(e.target.value);
								}}
							/>
							<div className="mt-1">
								<Button
									label="Save"
									showIcon={false}
									style={{
										width: '85px',
										height: '40px',
										backgroundColor: '#00203C',
										color: 'white',
									}}
									onClick={() => {
										handleUpdateDescription();
									}}
								/>
							</div>
						</div>
						<div className="flex items-center pt-2" style={{ justifyContent: 'end' }}>
							<div>
								{infrastructure?.parameters && infrastructure?.parameters.length > 0 && (
									<Button
										variant="primary"
										size="small"
										label="Add parameters"
										iconName="plus-circle"
										iconColor="white"
										onClick={() => {
											setIsNewParameter(true);
											setEditParamater({});
											showModal();
										}}
									/>
								)}
							</div>
						</div>
						<div
							className="pt-2"
							style={{
								height: 'calc(100vh - 430px)',
								display: 'flex',
							}}
						>
							{infrastructure?.parameters && infrastructure?.parameters.length > 0 ? (
								<Table
									columns={columnsTable}
									dataSource={infrastructure?.parameters}
									rowKey={(item) => item.id}
									scroll={{ x: true }}
								/>
							) : (
								<div
									className="flex flex-column self-center justify-center text-center"
									style={{ width: '100%' }}
								>
									<span className="text-title-page" style={{ marginBottom: '10px' }}>
										No parameters yet!
									</span>

                  <span
                    className="text-subtitle-page"
                    style={{ marginBottom: '10px' }}
                  >
                    Start by adding a few
									</span>
									<div className="self-center">
										<Button
											className="flex items-center ml-auto"
											label="Add parameter"
											style={{
												backgroundColor: '#023E65',
												color: 'white',
												width: '150px',
												height: '40px',
											}}
											onClick={() => {
												setIsNewParameter(true);
												setEditParamater({});
												showModal();
											}}
										/>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
				<ConfirmationModal
					title="Delete Parameter"
					message="Are you sure to delete it?"
					showModal={showConfirmDelete}
					onAccept={async () => {
						if (contextValue.selectedParam) {
							await parameterService.deleteParameter(contextValue.selectedParam);
							await handleGetInfrastructure();
							notification.success({
								message: 'Deleted successfully',
								description: 'Parameter deleted successfully',
								placement: 'topRight',
							});
							setShowConfirmDelete(false);
						}
					}}
					oncancel={() => {
						setShowConfirmDelete(false);
					}}
					showWarning
				/>
				{showModalParameterDetail && (
					<ModalParameterDetail
						open={showModalParameterDetail}
						onClose={() => {
							setContextValue({ ...contextValue, selectedParam: null });
							handleGetInfrastructure();
							setShowModalParameterDetail(false);
							// Limpiar parameterId de la URL al cerrar el modal
							setSearchParams((prev) => {
								const newParams = new URLSearchParams(prev);
								newParams.delete('parameterId');
								return newParams;
							});
						}}
						infrastructure={infrastructure}
						handleLoadSources={handleGetInfrastructure}
					/>
				)}
				{isModalOpen && (
					<AddInfrastructureModal
						isModalOpen={isModalOpen}
						handleOk={handleOk}
						handleCancel={handleCancel}
						handleGetInfrastructure={handleGetInfrastructure}
						isNewParameter={isNewParameter}
						editParameter={contextValue.selectedParam}
						setIsModalOpen={setIsModalOpen}
						infraId={infraId}
						infraType={infrastructure?.infraTypeId}
						parameters={infrastructure?.parameters}
					/>
				)}
			</>
		</InfraestructureContext.Provider>
	);
};

export default ParameterView;
